import { useEffect, useState } from 'react'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { Flex, colors } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Card, CardBody } from 'components/Card/Card'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import {
  getFoodEmployeeProfile,
  getFoodEmployeeProfiles,
  getFoodKitchens,
  getFoodMealWindows,
  upsertFoodEmployeeProfile,
} from 'features/food/foodSlice'
import { FoodProvisioningMode, IEmployeeFoodProfile } from 'types/api/foodApi.interface'
import { ISelectValue } from 'constants/globalTypes'
import { useHasPermission } from 'hooks/useHasPermission'
import {
  FOOD_PROVISIONING_MODE_OPTIONS,
  foodProvisioningModeLabel,
} from '../foodPlanningData'

const parseBasicUsers = (data: { data: { id: number; fullName: string }[] }) =>
  data.data.map((i) => ({ label: i.fullName, value: i.id }))

const profileUserId = (p: IEmployeeFoodProfile) => p.user?.id ?? null

const profileUserName = (p: IEmployeeFoodProfile) => {
  if (!p.user) return 'Unknown user'
  const composed = `${p.user.firstName || ''} ${p.user.lastName || ''}`.trim()
  return composed || `User #${p.user.id}`
}

export const ProfilesTab = () => {
  const dispatch = useDispatch<AppDispatch>()
  const canKitchen = useHasPermission('kitchen')
  const {
    employeeFoodProfiles,
    selectedEmployeeFoodProfile,
    logisticsKitchens,
    mealWindows,
  } = useSelector((store: RootState) => store.food)

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [selectedUserLabel, setSelectedUserLabel] = useState('')
  const [searchEmployee, setSearchEmployee] = useState<ISelectValue | null>(null)
  const [stickyKitchenId, setStickyKitchenId] = useState<number | null>(null)
  const [windowModes, setWindowModes] = useState<{ windowDefId: number; mode: number }[]>([])

  useEffect(() => {
    dispatch(getFoodEmployeeProfiles({ page: 1, limit: 200 }))
    dispatch(getFoodMealWindows({ page: 1, limit: 200 }))
    if (canKitchen) {
      dispatch(getFoodKitchens({ page: 1, limit: 200 }))
    }
  }, [dispatch, canKitchen])

  const selectEmployee = (v: ISelectValue) => {
    if (!v?.value) {
      setSearchEmployee(null)
      return
    }
    const userId = Number(v.value)
    const label = String(v.label || '')
    const exists = (employeeFoodProfiles.data || []).some((p) => profileUserId(p) === userId)
    setSearchEmployee(v)
    setSelectedUserId(userId)
    setSelectedUserLabel(label)
    if (!exists) {
      setStickyKitchenId(null)
      setWindowModes(
        (mealWindows.data || []).map((w) => ({
          windowDefId: w.id,
          mode: FoodProvisioningMode.MessOrHall,
        })),
      )
    }
  }

  useEffect(() => {
    if (!selectedUserId && (employeeFoodProfiles.data || []).length > 0) {
      const first = employeeFoodProfiles.data[0]
      const id = profileUserId(first)
      if (id != null) {
        setSelectedUserId(id)
        setSelectedUserLabel(profileUserName(first))
      }
    }
  }, [employeeFoodProfiles.data, selectedUserId])

  useEffect(() => {
    if (selectedUserId == null) return
    const exists = (employeeFoodProfiles.data || []).some((p) => profileUserId(p) === selectedUserId)
    if (exists) {
      dispatch(getFoodEmployeeProfile(selectedUserId))
    }
  }, [dispatch, selectedUserId, employeeFoodProfiles.data])

  useEffect(() => {
    if (!selectedEmployeeFoodProfile || profileUserId(selectedEmployeeFoodProfile) !== selectedUserId) {
      return
    }
    setStickyKitchenId(selectedEmployeeFoodProfile.stickyKitchen?.id ?? null)
    if (selectedEmployeeFoodProfile.user) {
      setSelectedUserLabel(profileUserName(selectedEmployeeFoodProfile))
    }
    const defaults = selectedEmployeeFoodProfile.windowModeDefaults || []
    const byWindow = (mealWindows.data || []).map((w) => {
      const existing = defaults.find((d) => d.windowDefId === w.id)
      return {
        windowDefId: w.id,
        mode: existing?.mode ?? FoodProvisioningMode.MessOrHall,
      }
    })
    setWindowModes(
      byWindow.length
        ? byWindow
        : defaults.map((d) => ({ windowDefId: d.windowDefId, mode: d.mode })),
    )
  }, [selectedEmployeeFoodProfile, selectedUserId, mealWindows.data])

  const kitchenOptions = [
    { label: 'None', value: 0 },
    ...(logisticsKitchens.data || []).map((k) => ({ label: k.name, value: k.id })),
  ]

  const handleSave = async () => {
    if (selectedUserId == null) return
    await dispatch(
      upsertFoodEmployeeProfile({
        userId: selectedUserId,
        body: {
          stickyKitchenId: stickyKitchenId || null,
          windowModeDefaults: windowModes,
        },
      }),
    )
    dispatch(getFoodEmployeeProfiles({ page: 1, limit: 200 }))
  }

  const profiles = employeeFoodProfiles.data || []
  const displayName =
    selectedUserLabel ||
    (selectedUserId != null
      ? profiles.find((p) => profileUserId(p) === selectedUserId)
        ? profileUserName(profiles.find((p) => profileUserId(p) === selectedUserId)!)
        : `User #${selectedUserId}`
      : 'N/A')

  return (
    <Flex gap='1rem' width='100%' main>
      <Flex width='40%' minWidth='20rem' column gap='1rem'>
        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Employee Food Profiles</TitleText>
          </ContainerHeader>
          <ContainerContent gap='0.75rem'>
            <HonestSelectAsyncInput
              nomarg
              labelText='Employee'
              link='users/basic'
              name='employee'
              placeholder='Search employee…'
              parserFunction={parseBasicUsers}
              defaultValue={searchEmployee?.label || ''}
              onChange={selectEmployee}
            />
            {profiles.map((p) => {
              const id = profileUserId(p)
              return (
                <Card
                  key={p.id}
                  onClick={() => {
                    if (id == null) return
                    setSelectedUserId(id)
                    setSelectedUserLabel(profileUserName(p))
                  }}
                  fullBorder={selectedUserId === id ? colors.orange : undefined}
                  hoverBorder
                >
                  <CardBody>
                    <BodyText weight='600'>{profileUserName(p)}</BodyText>
                    <OtherText grey>
                      {p.windowModeDefaults?.length || 0} window mode
                      {(p.windowModeDefaults?.length || 0) === 1 ? '' : 's'}
                      {p.stickyKitchen
                        ? ` · ${p.stickyKitchen.name}`
                        : ' · No sticky kitchen'}
                    </OtherText>
                  </CardBody>
                </Card>
              )
            })}
            {!profiles.length && <OtherText grey>No profiles yet — search an employee above.</OtherText>}
          </ContainerContent>
        </Container>
      </Flex>

      <Flex width='100%'>
        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Edit Profile — {displayName}</TitleText>
          </ContainerHeader>
          {selectedUserId == null ? (
            <ContainerContent>
              <OtherText grey>Search and select an employee to view or create a profile.</OtherText>
            </ContainerContent>
          ) : (
            <ContainerContent gap='1rem'>
              {canKitchen && (
                <HonestSelectInput
                  nomarg
                  labelText='Sticky kitchen'
                  placeholder='Select kitchen…'
                  options={kitchenOptions}
                  defaultValue={stickyKitchenId || 0}
                  onChange={(v: ISelectValue) =>
                    setStickyKitchenId(v && Number(v.value) ? Number(v.value) : null)
                  }
                  name='stickyKitchen'
                />
              )}

              <OtherText grey>WINDOW MODE DEFAULTS (replaces entire set on save)</OtherText>
              {(mealWindows.data || []).map((w) => {
                const current =
                  windowModes.find((m) => m.windowDefId === w.id)?.mode ??
                  FoodProvisioningMode.MessOrHall
                return (
                  <Flex key={w.id} between center gap='1rem' width='100%'>
                    <BodyText>{w.name}</BodyText>
                    <HonestSelectInput
                      nomarg
                      width='14rem'
                      labelText='Mode'
                      placeholder='Select mode…'
                      options={FOOD_PROVISIONING_MODE_OPTIONS}
                      defaultValue={current}
                      onChange={(v: ISelectValue) =>
                        setWindowModes((prev) => {
                          const next = prev.filter((m) => m.windowDefId !== w.id)
                          return [...next, { windowDefId: w.id, mode: Number(v.value) }]
                        })
                      }
                      name={`mode-${w.id}`}
                    />
                  </Flex>
                )
              })}

              {selectedEmployeeFoodProfile?.windowModeDefaults?.length > 0 && (
                <Flex wrap gap='0.25rem'>
                  {selectedEmployeeFoodProfile.windowModeDefaults.map((d) => (
                    <StatusPill key={d.id} status='info'>
                      {`${d.windowDefName}: ${foodProvisioningModeLabel(d.mode)}`}
                    </StatusPill>
                  ))}
                </Flex>
              )}

              <Button variant='primary' onClick={handleSave}>
                Save profile
              </Button>
            </ContainerContent>
          )}
        </Container>
      </Flex>
    </Flex>
  )
}
